import React, { useCallback, useEffect, useState } from "react";
import { Box, Typography, CircularProgress, Button } from "@mui/material";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";

import ChefForPartyService from "../../services/ChefForPartyService";
import PartyAllocationMetaShow from "./PartyAllocationMetaShow";
import { CustomerService } from "../../services/CustomerService";
import CustomerPreferenceSummary from "../Customers/CustomerPreferenceSummary";

interface PartyServiceAllocationTabProps {
  record: any;
}

const chefForPartyService = new ChefForPartyService();
const customerService = new CustomerService();

const PartyServiceAllocationTab: React.FC<PartyServiceAllocationTabProps> = ({
  record,
}) => {
  const [metaData, setMetaData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [customerPreference, setCustomerPreference] = useState<any>(null);
  const [blockedCooks, setBlockedCooks] = useState<any[]>([]);
  const [preferredCooks, setPreferredCooks] = useState<any[]>([]);

  useEffect(() => {
    if (record?.id) {
      fetchAcceptedLeads(record.id);
    }
  }, [record?.id]);

  const fetchAcceptedLeads = async (serviceId: number) => {
    try {
      setLoading(true);
      const response = await chefForPartyService.getAcceptedLeadForParty(
        serviceId,
        50, // limit (adjust as needed)
        0,  // offset
        { createdAt: "desc" }
      );
      setMetaData(response?.data ?? []);
    } catch (error) {
      console.error("Failed to fetch accepted leads for party:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAutoAllocateCookList = async () => {
    setGenerating(true);
    try {
      await chefForPartyService.generateAutoAllocateCookList(record.id);
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Auto allocate cook list task queued successfully",
        type: CustomSnackbarTypes.SUCCESS,
      });
      fetchAcceptedLeads(record.id);
    } catch (err: any) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: err?.response?.data?.message || "Failed to generate cook list",
        type: CustomSnackbarTypes.ERROR,
      });
    } finally {
      setGenerating(false);
    }
  };

  const refreshData = useCallback(
    () => {
    const customerId = Number(record?.customerId);
    if (!customerId) return;

    customerService
        .getCustomerPrefernceByCustomerId(customerId)
        .then((data) => {
        setCustomerPreference(data);
        })
        .catch((err: any) => {
        });

    // Cooks the customer has blocked — never allocate these.
    customerService
        .getBlockedCooksByCustomerId(customerId)
        .then((data) => setBlockedCooks(Array.isArray(data) ? data : []))
        .catch(() => setBlockedCooks([]));

    // Cooks the customer prefers (ranked, ACTIVE) — surface these first.
    customerService
        .getCustomerCookPrefernces(customerId)
        .then((data) => setPreferredCooks(Array.isArray(data) ? data : []))
        .catch(() => setPreferredCooks([]));
    },
    [record?.customerId]
);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  return (
    <Box position="relative">
      {loading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          justifyContent="center"
          alignItems="center"
          bgcolor="rgba(255,255,255,0.6)"
          zIndex={10}
        >
          <CircularProgress />
        </Box>
      )}
      <CustomerPreferenceSummary
          customerPreference={customerPreference}
          customerId={record?.customerId}
          blockedCooks={blockedCooks}
          preferredCooks={preferredCooks}
      />
      {metaData.length ? (
        <PartyAllocationMetaShow
          data={metaData}
          trail={record}
          blockedCooks={blockedCooks}
          preferredCooks={preferredCooks}
          reload={() => fetchAcceptedLeads(record.id)}
        />
      ) : (
        <Box p={2} display="flex" flexDirection="column" alignItems="flex-start" gap={2}>
          <Typography>No accepted leads found</Typography>
          <Button
            variant="contained"
            onClick={handleGenerateAutoAllocateCookList}
            disabled={generating}
          >
            {generating ? "Generating..." : "Generate Auto Allocate Cook List"}
          </Button>
        </Box>
      )}
    </Box>
  );  
};

export default PartyServiceAllocationTab;
